import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Building,
  BuildingEditForm,
  CreateBuilding,
  buildingZod,
  useCreateBuildingMutation,
  useGetBuildingQuery,
  useUpdateBuildingMutation,
} from 'entities/building';
import { useCreateChessMutation } from 'entities/chess';
import {
  Entrance,
  useCreateEntranceMutation,
  useDeleteEntranceMutation,
  useDuplicateEntranceMutation,
  useUpdateEntranceMutation,
} from 'entities/entrance';
import { useVersionCheck } from 'entities/object';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { CardLayout } from 'shared/ui/CardLayout';
import { EditBuildingSkeleton } from './EditBuildingSkeleton';

enum EntranceStatus {
  Added = 'added',
  Duplicated = 'duplicated',
  Updated = 'updated',
  Deleted = 'deleted',
  Unchanged = 'unchanged',
}

interface EntranceWithStatus extends Entrance {
  status: EntranceStatus;
  originalId?: number | null;
}

interface EditBuildingProps {
  buildingId?: number;
  onCreated?: () => void;
  onUpdated?: () => void;
  onCancelClick?: () => void;
}

const editBuildingZod = buildingZod
  .pick({
    name: true,
    status: true,
    actualCompleteDate: true,
    completeQuarter: true,
    completeYear: true,
  })
  .refine(
    (values) => {
      if (values.status === '1' && !values.actualCompleteDate) {
        return false;
      }
      return true;
    },
    {
      message: 'building.form.error.actualCompleteDate',
      path: ['actualCompleteDate'],
    }
  );

export const EditBuilding: FC<EditBuildingProps> = ({
  buildingId,
  onCreated,
  onUpdated,
  onCancelClick,
}) => {
  const { t } = useTranslation();
  const { objectId } = useParams<{ objectId: string }>();
  const { data: storedBuilding, isLoading } = useGetBuildingQuery(
    buildingId || skipToken
  );

  const [buildingEntrances, setBuildingEntrances] = useState<
    EntranceWithStatus[]
  >([]);

  const { checkVersion, showVersionChangedPopup } = useVersionCheck(objectId);

  const { showSnackbar } = useSnackbar();

  const form = useForm<Building>({
    resolver: zodResolver(editBuildingZod),
    mode: 'onSubmit',
    defaultValues: {
      name: storedBuilding?.name || '',
      status: storedBuilding?.status || '',
      actualCompleteDate: storedBuilding?.actualCompleteDate || null,
      completeQuarter: storedBuilding?.completeQuarter || '',
      completeYear: storedBuilding?.completeYear || '',
    },
  });

  useEffect(() => {
    if (storedBuilding?.entrances) {
      const entrancesWithStatus: EntranceWithStatus[] =
        storedBuilding.entrances.map((entrance) => ({
          ...entrance,
          status: EntranceStatus.Unchanged,
        }));
      setBuildingEntrances(entrancesWithStatus);
    }
  }, [storedBuilding]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (
        values.status &&
        ['2', '3', '4'].includes(values.status) &&
        values.actualCompleteDate
      ) {
        form.setValue('actualCompleteDate', null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const [createBuilding, { isLoading: isBuildingCreating }] =
    useCreateBuildingMutation();
  const [updateBuilding, { isLoading: isBuildingUpdating }] =
    useUpdateBuildingMutation();
  const [createEntrance, { isLoading: isEntranceCreating }] =
    useCreateEntranceMutation();
  const [updateEntrance, { isLoading: isEntranceUpdating }] =
    useUpdateEntranceMutation();
  const [createChess, { isLoading: isChessCreating }] =
    useCreateChessMutation();
  const [duplicateEntrance, { isLoading: isEntranceDuplicating }] =
    useDuplicateEntranceMutation();
  const [deleteEntrance] = useDeleteEntranceMutation();

  const hasEntrances = buildingEntrances.some(
    (entrance) => entrance.status !== EntranceStatus.Deleted
  );

  const handleUpdateBuildingClick = async (buildingData: Building) => {
    const isVersionChanged = await checkVersion();

    if (isVersionChanged) return;

    if (!hasEntrances) {
      form.setError('root', {
        message: t('building.form.error.addEntrance'),
      });
      return;
    }

    const preparedBuilding = { ...storedBuilding, ...buildingData };

    try {
      for (const entrance of buildingEntrances) {
        const isTemporaryId =
          typeof entrance.id === 'number' && entrance.id > 1_000_000_000;

        switch (entrance.status) {
          case EntranceStatus.Added: {
            const newEntrance = await createEntrance({
              entrance: entrance,
              objectId: Number(objectId),
            }).unwrap();

            await createChess({
              chess: {
                entranceId: newEntrance.id,
                floorsCount: Number(newEntrance.floorCount),
                risersCount: Number(newEntrance.riserCount),
              },
              objectId: Number(objectId),
              buildingId: Number(buildingId),
            });
            break;
          }

          case EntranceStatus.Updated: {
            const entrancePayload = {
              ...entrance,
              buildingId: Number(buildingId),
            };
            if (!isTemporaryId) {
              await updateEntrance(entrancePayload).unwrap();
            }
            break;
          }

          case EntranceStatus.Duplicated: {
            if (typeof entrance.originalId !== 'number') {
              throw new Error(
                'Duplicated entrance is missing a valid originalId'
              );
            }

            const entrancePayload = {
              ...entrance,
              id: entrance.originalId,
              buildingId: Number(buildingId),
            };
            await duplicateEntrance(entrancePayload).unwrap();
            break;
          }

          case EntranceStatus.Deleted: {
            if (!isTemporaryId) {
              await deleteEntrance({
                entrance,
                objectId: Number(objectId),
              }).unwrap();
            }
            break;
          }

          default:
            break;
        }
      }

      await updateBuilding(preparedBuilding).unwrap();
      onUpdated?.();
    } catch (error) {
      console.error(error);
      showSnackbar();
    }
  };

  const handleCreateBuildingClick = async (buildingData: CreateBuilding) => {
    const isVersionChanged = await checkVersion();

    if (!isVersionChanged) {
      const activeEntrances = buildingEntrances.filter(
        (e) => e.status !== EntranceStatus.Deleted
      );

      if (activeEntrances.length === 0) {
        form.setError('root', {
          message: t('building.form.error.addEntrance'),
        });
        return;
      }

      const entrances = activeEntrances.map(
        ({ name, floorCount, riserCount }) => ({
          name,
          floorCount,
          riserCount,
        })
      );

      const newBuildingData = {
        ...buildingData,
        objectId: Number(objectId),
        entrances,
      };

      try {
        await createBuilding(newBuildingData).unwrap();
        onCreated?.();
      } catch (error) {
        console.error(error);
        showSnackbar();
      }
    }
  };

  const handleSaveEntrance = async (entrance: Entrance) => {
    const isVersionChanged = await checkVersion();

    if (!isVersionChanged) {
      setBuildingEntrances((prev) => {
        const exists = prev.some((e) => e.id === entrance.id);

        if (exists) {
          return prev.map((item) => {
            if (item.id !== entrance.id) return item;

            const status =
              item.status === EntranceStatus.Added
                ? EntranceStatus.Added
                : EntranceStatus.Updated;

            return { ...entrance, status };
          });
        }

        return [
          ...prev,
          {
            ...entrance,
            id: Date.now(),
            status: EntranceStatus.Added,
          },
        ];
      });
    }
  };

  const handleRemoveEntrance = async (entrance: Entrance) => {
    const isVersionChanged = await checkVersion();

    if (!isVersionChanged) {
      setBuildingEntrances((prev) => {
        const updated = prev.map((e) =>
          e.id === entrance.id ? { ...e, status: EntranceStatus.Deleted } : e
        );

        const deletedId = entrance.id;

        return updated.map((e) => {
          if (
            e.status === EntranceStatus.Duplicated &&
            e.originalId === deletedId
          ) {
            return {
              ...e,
              status: EntranceStatus.Added,
              originalId: undefined,
            };
          }
          return e;
        });
      });
    }
  };

  const onDuplicateEntrance = async (entrance: Entrance) => {
    const isVersionChanged = await checkVersion();

    if (!isVersionChanged) {
      setBuildingEntrances((prev) => {
        const foundEntrance = prev.find((e) => e.id === entrance.id);
        if (foundEntrance) {
          const isOriginalAdded = foundEntrance.status === EntranceStatus.Added;

          const duplicated: EntranceWithStatus = {
            ...foundEntrance,
            id: Date.now(),
            status: isOriginalAdded
              ? EntranceStatus.Added
              : EntranceStatus.Duplicated,
            originalId: isOriginalAdded
              ? undefined
              : foundEntrance.originalId ?? foundEntrance.id,
          };

          return [...prev, duplicated];
        }
        return prev;
      });
    }
  };

  useEffect(() => {
    if (!isLoading && storedBuilding) {
      form.reset({
        name: storedBuilding?.name || '',
        status: storedBuilding?.status || '',
        actualCompleteDate: storedBuilding?.actualCompleteDate || '',
        completeQuarter: storedBuilding?.completeQuarter || '',
        completeYear: storedBuilding?.completeYear || '',
      });
    }
  }, [isLoading, storedBuilding, form]);

  useEffect(() => {
    if (hasEntrances) {
      form.clearErrors('root');
    }
  }, [form, hasEntrances]);

  if (isLoading) return <EditBuildingSkeleton />;

  return (
    <CardLayout
      header={{
        title: buildingId
          ? t('building.form.title.editingBuilding')
          : t('building.form.title.addingBuilding'),
        subtitle: t('building.form.subtitle'),
      }}
      footer={{
        onOkClick: buildingId
          ? form.handleSubmit(handleUpdateBuildingClick)
          : form.handleSubmit(handleCreateBuildingClick),
        onCancelClick: onCancelClick,
        okLabel: t('ui.common.save'),
        cancelLabel: t('ui.common.cancel'),
        okButtonColor: 'primary',
        okButtonLoading:
          isBuildingCreating ||
          isBuildingUpdating ||
          isEntranceCreating ||
          isChessCreating,
      }}
      errors={
        form.formState.errors.root?.message
          ? [form.formState.errors.root?.message]
          : []
      }
    >
      {showVersionChangedPopup()}
      <FormProvider {...form}>
        <BuildingEditForm
          building={storedBuilding}
          entrances={buildingEntrances.filter(
            (entrance) => entrance.status !== EntranceStatus.Deleted
          )}
          onSaveEntrance={handleSaveEntrance}
          onRemoveEntrance={handleRemoveEntrance}
          onDuplicateEntrance={onDuplicateEntrance}
          isEntranceSaving={
            isEntranceCreating || isEntranceUpdating || isEntranceDuplicating
          }
        />
      </FormProvider>
    </CardLayout>
  );
};
